// Right pane: who this is, their zone, opt-out state, assign / close, notes, reminders.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPhone } from '@bt/core';
import { api, type Contact, type Conversation, type MeResponse, type OptOut, type UserSummary } from '../../api';
import { InlineError } from '../../components/States';
import { useToast } from '../../components/Toast';
import { fmtFull, zoneLabel } from '../../lib/format';
import { COMMON_TIMEZONES, TZ_SOURCE_WORDS, effectiveTimezone } from '../../lib/timezone';
import { UnblockDialog } from '../../components/UnblockDialog';
import { ReminderDialog } from '../scheduled/ReminderDialog';

export function ContactPanel({
  conversation,
  contact,
  optedOut,
  optOut,
  me,
  users,
}: {
  conversation: Conversation;
  contact: Contact;
  optedOut: boolean;
  optOut: OptOut | null;
  me: MeResponse;
  users: UserSummary[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editingName, setEditingName] = useState(false);
  const [name, setName] = useState(contact.name ?? '');
  const [editingTz, setEditingTz] = useState(false);
  const [assignTo, setAssignTo] = useState<string>(conversation.assigned_user_id ?? '');
  const [handoffNote, setHandoffNote] = useState('');
  const [note, setNote] = useState('');
  const [unblock, setUnblock] = useState(false);
  const [reminder, setReminder] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['conversation', conversation.id] });
    qc.invalidateQueries({ queryKey: ['conversations'] });
    qc.invalidateQueries({ queryKey: ['counts'] });
    qc.invalidateQueries({ queryKey: ['contact', contact.id] });
  };

  const updateContact = useMutation({
    mutationFn: (p: { name?: string | null; timezone?: string | null }) => api.contacts.update(contact.id, p),
    onSuccess: () => {
      refresh();
      setEditingName(false);
      setEditingTz(false);
    },
  });
  const assign = useMutation({
    mutationFn: (p: { user_id: string | null; note?: string }) => api.conversations.assign(conversation.id, p.user_id, p.note),
    onSuccess: (_d, p) => {
      refresh();
      setHandoffNote('');
      const who = users.find((u) => u.id === p.user_id)?.name;
      toast({ text: p.user_id ? `Handed to ${who ?? 'them'}.` : 'Unassigned.', tone: 'ok' });
    },
  });
  const status = useMutation({
    mutationFn: (s: 'open' | 'closed') => api.conversations.setStatus(conversation.id, s),
    onSuccess: refresh,
  });
  const addNote = useMutation({
    mutationFn: (body: string) => api.conversations.addNote(conversation.id, body),
    onSuccess: () => {
      setNote('');
      refresh();
    },
  });
  const block = useMutation({
    mutationFn: () => api.contacts.optOut(contact.id, 'block'),
    onSuccess: () => {
      refresh();
      toast({ text: 'Number blocked. No more texts will go out to it.', tone: 'ok' });
    },
  });

  const tz = effectiveTimezone(contact, me.workspace.timezone);
  const assignedDirty = assignTo !== (conversation.assigned_user_id ?? '');
  const activeUsers = users.filter((u) => !u.disabled_at || u.id === conversation.assigned_user_id);

  return (
    <div className="contact-panel">
      <div className="section">
        {editingName ? (
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              updateContact.mutate({ name: name.trim() || null });
            }}
          >
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder="Name" />
            <button className="btn small primary" type="submit" disabled={updateContact.isPending}>
              Save
            </button>
            <button className="btn small ghost" type="button" onClick={() => setEditingName(false)}>
              Cancel
            </button>
          </form>
        ) : (
          <div className="big-name inline-edit" onClick={() => setEditingName(true)} title="Click to edit the name" role="button" tabIndex={0}>
            {contact.name?.trim() || <span className="muted">Add a name</span>}
          </div>
        )}
        <div className="kv">{formatPhone(contact.phone_number)}</div>
        <div className="kv" style={{ marginTop: 6 }}>
          <div className="k">Time zone</div>
          {editingTz ? (
            <div className="row">
              <select className="input" defaultValue={contact.timezone ?? ''} onChange={(e) => updateContact.mutate({ timezone: e.target.value || null })}>
                <option value="">Automatic ({TZ_SOURCE_WORDS[tz.source === 'contact' ? 'workspace' : tz.source]})</option>
                {COMMON_TIMEZONES.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label}
                  </option>
                ))}
              </select>
              <button className="btn small ghost" onClick={() => setEditingTz(false)}>
                Done
              </button>
            </div>
          ) : (
            <div className="inline-edit" onClick={() => setEditingTz(true)} role="button" tabIndex={0} title="Click to change">
              {zoneLabel(tz.tz)} <span className="muted small">({TZ_SOURCE_WORDS[tz.source]})</span>
              {tz.candidates.length > 1 && <div className="tiny warn-text">This area code spans {tz.candidates.length} zones; texts wait until the window is open in all of them.</div>}
            </div>
          )}
        </div>
        <InlineError error={updateContact.error} />
      </div>

      <div className="section">
        {optedOut ? (
          <>
            <span className="chip danger">OPTED OUT</span>
            <div className="small muted" style={{ marginTop: 4 }}>
              {optOut ? (
                <>
                  {optOut.reason === 'stop_keyword' ? 'Replied STOP' : optOut.reason === 'manual' ? 'Blocked by staff' : 'Blocked by the carrier'} · {fmtFull(optOut.created_at, me.workspace.timezone)}
                </>
              ) : (
                'This number asked not to be texted.'
              )}
            </div>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => setUnblock(true)}>
              Unblock
            </button>
          </>
        ) : (
          <>
            <div className="small muted">Texting allowed</div>
            <button className="btn small danger" style={{ marginTop: 6 }} onClick={() => confirm(`Block ${formatPhone(contact.phone_number)}? No more texts will go out to this number.`) && block.mutate()} disabled={block.isPending}>
              Block this number
            </button>
            <InlineError error={block.error} />
          </>
        )}
      </div>

      <div className="section">
        <div className="field-label">Assigned to</div>
        <select className="input" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
          <option value="">Unassigned</option>
          {activeUsers.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
              {u.id === me.user.id ? ' (me)' : ''}
            </option>
          ))}
        </select>
        {assignedDirty && (
          <div className="stack" style={{ marginTop: 8 }}>
            {assignTo && assignTo !== me.user.id && (
              <textarea className="input" placeholder="Optional note for them: what they need to know" value={handoffNote} onChange={(e) => setHandoffNote(e.target.value)} rows={2} />
            )}
            <div className="row">
              <button className="btn small primary" disabled={assign.isPending} onClick={() => assign.mutate({ user_id: assignTo || null, note: handoffNote.trim() || undefined })}>
                {assignTo ? (assignTo === me.user.id ? 'Take it' : 'Hand off') : 'Unassign'}
              </button>
              <button className="btn small ghost" onClick={() => setAssignTo(conversation.assigned_user_id ?? '')}>
                Cancel
              </button>
            </div>
          </div>
        )}
        <InlineError error={assign.error} />
        <div className="row" style={{ marginTop: 10 }}>
          {conversation.status === 'open' ? (
            <button className="btn small" onClick={() => status.mutate('closed')} disabled={status.isPending}>
              Close conversation
            </button>
          ) : (
            <button className="btn small" onClick={() => status.mutate('open')} disabled={status.isPending}>
              Reopen
            </button>
          )}
        </div>
        <InlineError error={status.error} />
      </div>

      <div className="section">
        <div className="field-label">Add a note (only staff see it)</div>
        <textarea className="input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="e.g. prefers afternoons, gate code 1234" />
        <button className="btn small" style={{ marginTop: 6 }} disabled={!note.trim() || addNote.isPending} onClick={() => addNote.mutate(note.trim())}>
          Add note
        </button>
        <InlineError error={addNote.error} />
      </div>

      <div className="section">
        <button className="btn small" onClick={() => setReminder(true)} disabled={optedOut} title={optedOut ? 'This number opted out' : undefined}>
          Schedule reminder
        </button>
        <div style={{ marginTop: 8 }}>
          <Link to={`/contacts/${contact.id}`} className="small">
            Open contact page →
          </Link>
        </div>
      </div>

      {unblock && <UnblockDialog contact={contact} onClose={() => setUnblock(false)} onDone={refresh} />}
      {reminder && <ReminderDialog me={me} contact={contact} lineId={conversation.line_id} onClose={() => setReminder(false)} />}
    </div>
  );
}
